import React, { ChangeEvent, Dispatch, Fragment, SetStateAction, useRef, useState } from 'react';
import { JSX } from 'react/jsx-runtime';
import clsx from 'clsx';
import { Listbox, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import isEmpty from 'lodash/isEmpty';
import { FormikErrors } from 'formik';
import { motion } from 'framer-motion';
import { ScrollToFormikError } from '../../../../components/elements/ScrollToFormikError';
import useIntermediaries from '../../../../hooks/intermediariesHook';
import { getPolicyIssuanceModeDescriptionFromEnum } from '../../../../utils/miscFunctions';
import { POLICY_ISSUANCE_MODES_WITH_DESCRIPTION } from '../../../../resources/constants';
import Intermediary from '../../../../models/Intermidiary';
import { InfoIcon } from 'lucide-react';
import ToolTip from '../../../../components/ToolTip';

export interface IIntermediarySelectorInputItem {
    id?: string;
    name: string;
    value: string | number | boolean | null;
    description?: string;
    title?: any
}

export interface IIIntermediarySelectorInputItemProps {
    selectInputClassName?: string;
    label?: string;
    listSelectedValue?: number | string | boolean | null;
    showAvatar?: boolean;
    onChange: Dispatch<SetStateAction<IIntermediarySelectorInputItem | null>> | any;
    onBlur?: (e: React.FocusEvent<any, Element>) => void | null | any;
    list?: Array<IIntermediarySelectorInputItem>;
    grouped?: boolean;
    groupedList?: Record<string, Array<IIntermediarySelectorInputItem>>;
    placeholder?: string;
    description?: JSX.Element | React.ReactElement | string | number | any;
    name?: string;
    error?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
    required?: boolean;
    helperText?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
    disabled?: boolean;
    sort?: boolean;
    emptyListMessage?: string;
    searchPlaceHolder?: string;
    wrapperClasses?: string;
    IconClass?: string;
    prepend?: React.ReactNode;
    classes?: string;
    // Optional fallback: guarantees the dropdown can display an insurer that
    // isn't in the user's own intermediaries feed (e.g. broker viewing a
    // bulk-imported policy for an insurer they don't have a relationship
    // with). Kept as a small { id, name } shape so callers don't have to
    // fabricate a fake Intermediary row. `id` is loose (string | number)
    // because the FE Insurer/Policy models don't consistently type the id
    // as a number — coerced below before we insert into the deduped list.
    fallbackInsurer?: { id: string | number | null | undefined; name: string | null | undefined };
}

function IntermediarySelectorInput<T>({
    selectInputClassName,
    label = 'Insurance Company',
    listSelectedValue,
    onChange,
    description,
    name,
    error,
    disabled = false,
    required = true,
    helperText,
    onBlur,
    sort = false,
    searchPlaceHolder = 'Select insurance company...',
    wrapperClasses,
    placeholder,
    emptyListMessage,
    IconClass,
    prepend,
    classes,
    fallbackInsurer,
}: IIIntermediarySelectorInputItemProps): JSX.Element {
    const [searchValue, setSearchValue] = useState<string>('');
    const fieldRef = useRef<HTMLDivElement>(null);
    const [searchResult, setSearchResult] = useState<Array<IIntermediarySelectorInputItem>>([]);
    const { intermediaries } = useIntermediaries()

    const getBadgeColor = (mode: string) => {
        switch (mode) {
            case 'MID_LIVE':
                return 'bg-green-100 text-green-800';
            case 'OFFLINE':
                return 'bg-gray-100 text-gray-800';
            case 'API':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // The intermediaries feed carries one row per (insurer × issuance mode
    // × broker relationship), so the same insurer shows up multiple times
    // when they run several modes (e.g. Enterprise on both MID_LIVE and
    // INSURER_API). The dropdown's selected value is only the insurerId —
    // the mode is picked/resolved later — so surface one row per insurer.
    //
    // Preference order picks the "most live" mode when the same insurer
    // has several: MID_LIVE beats INSURER_API beats EMAIL beats OFFLINE.
    // That mirrors how downstream code routes policy issuance, so the
    // badge next to the insurer name matches what the operator will
    // actually use.
    const modePreference: Record<string, number> = {
        MID_LIVE: 0,
        INSURER_API: 1,
        EMAIL: 2,
        OFFLINE: 3,
    };
    const dedupedByInsurer = new Map<number, Intermediary>();
    (intermediaries ?? []).forEach((intermediary: Intermediary) => {
        const existing = dedupedByInsurer.get(intermediary.insurerId);
        if (!existing) {
            dedupedByInsurer.set(intermediary.insurerId, intermediary);
            return;
        }
        const existingRank = modePreference[existing.issuanceMode as unknown as string] ?? 99;
        const nextRank = modePreference[intermediary.issuanceMode as unknown as string] ?? 99;
        if (nextRank < existingRank) {
            dedupedByInsurer.set(intermediary.insurerId, intermediary);
        }
    });

    const formattedList: IIntermediarySelectorInputItem[] = Array.from(dedupedByInsurer.values()).map((intermediary: Intermediary) => {
        const mode = intermediary.issuanceMode;
        const badgeColor = getBadgeColor(mode);
        const description = getPolicyIssuanceModeDescriptionFromEnum(mode);

        return {
            name: intermediary.insurerName,
            value: intermediary.insurerId,
            description: description,
            title: mode ? (
                <div className="flex items-center gap-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
                        {POLICY_ISSUANCE_MODES_WITH_DESCRIPTION[mode]?.title || mode?.replace(/_/g, ' ')}
                    </span>
                    <ToolTip message={description}>
                        <InfoIcon className="hidden md:block h-3 w-3 text-gray-400 hover:text-gray-600" />
                    </ToolTip>
                </div>
            ) : undefined
        };
    });

    // Broker users get a filtered intermediaries feed limited to insurers
    // they hold a partnership with. Bulk-imported policies (or policies
    // reassigned) can carry an insurer outside that set — the dropdown
    // then can't find a matching item and renders blank even though the
    // Formik value is populated. Splice the current insurer in so the
    // prefilled value has something to render against; downstream === on
    // insurerId still works because we use the same numeric id.
    if (fallbackInsurer
        && fallbackInsurer.id != null
        && fallbackInsurer.name) {
        // Coerce to number when possible so downstream code that reads
        // the selected value as a numeric insurerId (create-policy /
        // endorse payloads) keeps working. Fall back to the string id if
        // Number() doesn't produce a finite value.
        const rawId = fallbackInsurer.id;
        const numericId = typeof rawId === 'number' ? rawId : Number(rawId);
        const resolvedId: string | number = Number.isFinite(numericId) ? numericId : String(rawId);
        // Loose (String()) dedupe so we don't double-add when the feed
        // already has this insurer under a differently-typed id.
        if (!formattedList.some((it) => String(it.value ?? '') === String(resolvedId))) {
            formattedList.unshift({
                name: fallbackInsurer.name,
                value: resolvedId,
                description: '',
            });
        }
    }

    const searchedList = isEmpty(searchResult) && searchValue.length < 1 ? formattedList : searchResult;

    const onSearchChange = (evt: ChangeEvent<HTMLInputElement> | undefined) => {
        const val = evt?.target.value ?? '';

        setSearchValue(val);
        const srchList: Array<IIntermediarySelectorInputItem> = [];

        formattedList?.forEach((listItem) => {
            if (listItem.name?.toString().toLowerCase().includes(val.toLowerCase())) srchList.push(listItem);
        });
        setSearchResult(srchList);
    };

    // Loose match by String() coercion. The intermediaries feed returns
    // insurerId as a number, but callers frequently store the Formik value
    // as a string (Yup.string() validation) or pass String(insurer.id)
    // through wizardData. Strict === was silently failing on "4" !== 4
    // and rendering the dropdown blank even though both sides carried the
    // right id.
    const matchesSelected = (itemValue: unknown) =>
        String(itemValue ?? '') === String(listSelectedValue ?? '');

    const getListItemDisplayName = () => {
        return formattedList?.find((listItem: IIntermediarySelectorInputItem) => matchesSelected(listItem.value))?.name;
    };

    return (
        <Listbox
            ref={fieldRef}
            value={
                formattedList?.find((listItem: IIntermediarySelectorInputItem) => matchesSelected(listItem.value))?.value
            }
            onChange={onChange}
            disabled={disabled}
            as={'div'}
            className={clsx({ ['animate-wiggle']: error && required }, wrapperClasses)}>
            {({ open }) => (
                <>
                    {/* Label */}
                    {label && (
                        <Listbox.Label
                            className={clsx('block text-sm font-medium text-gray-700 dark:text-darkBg-mainText whitespace-nowrap', {
                                ['!text-danger-main']: error && required
                            })}>
                            {label} <span className="!text-danger-main">{required && '*'}</span>
                            {description && (
                                <p
                                    className={clsx('text-xs text-gray-400 dark:text-darkBg-subText italic', {
                                        ['text-danger-border']: error && required
                                    })}>
                                    {description}
                                </p>
                            )}
                        </Listbox.Label>
                    )}

                    <div className={clsx(`mt-1 relative`, classes)}>
                        {/* Input Field */}
                        <div>
                            <Listbox.Button
                                name={name}
                                className={clsx(
                                    'relative w-full bg-white dark:bg-darkBg border border-gray-300 dark:border-darkBg-border shadow-sm pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-main focus:border-primary-border sm:text-sm rounded-full',
                                    { ['bg-red-200 !border-danger-main focus:ring-danger-main focus:border-danger-border']: error && required }, selectInputClassName)}
                                onBlur={!open ? onBlur : (e: any) => null}>

                                {prepend && (
                                    <div
                                        className={clsx('absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none w-12 sm:w-15 h-full text-[#004E60]',)}>
                                        {prepend}
                                    </div>
                                )}

                                {listSelectedValue ? (
                                    <span className={clsx("block truncate dark:text-darkBg-mainText", prepend ? 'ml-10' : 'ml-3')}>
                                        {getListItemDisplayName()}
                                    </span>
                                ) : (
                                    <span className={clsx("text-gray-400 dark:text-darkBg-subText text-sm", prepend ? 'pl-12' : 'pl-3')}>
                                        {placeholder ? placeholder : 'Select Option'}
                                    </span>
                                )}

                                {!disabled && (
                                    <span className="ml-3 absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none border-none">
                                        {open ? (
                                            <ChevronUpIcon
                                                className={clsx('h-5 w-5 text-gray-400 dark:text-darkBg-subText', IconClass)}
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            <ChevronDownIcon
                                                className={clsx('h-5 w-5 text-gray-400 dark:text-darkBg-subText', IconClass)}
                                                aria-hidden="false"
                                            />
                                        )}
                                    </span>
                                )}
                            </Listbox.Button>


                        </div>

                        <Transition
                            show={open}
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0">
                            <Listbox.Options className="absolute z-40 mt-1 w-full bg-white dark:bg-darkBg shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5  focus:outline-none sm:text-sm">
                                {isEmpty(formattedList) ? (
                                    // formattedList Empty
                                    <div className="w-full flex justify-center items-center h-20 flex-col">
                                        <h4 className="font-medium !text-red-400">No Option&#40;s&#41; Available!</h4>
                                        <small className="text-xs text-gray-500 dark:text-darkBg-subText">
                                            {emptyListMessage ? emptyListMessage : 'Refresh to try again'}
                                        </small>
                                    </div>
                                ) : (
                                    <>
                                        {/* Search */}
                                        {(formattedList && formattedList?.length > 4) && (
                                            <div className="px-2">
                                                <div className="py-1 border-b-2 border-gray-200 dark:border-darkBg-border">
                                                    <div className="absolute top-5 left-5  flex items-center pointer-events-none">
                                                        <span className="sm:text-sm">
                                                            <MagnifyingGlassIcon className="h-5 w-5 stroke-gray-400 dark:stroke-darkBg-subText" />
                                                        </span>
                                                    </div>

                                                    <input
                                                        type="search"
                                                        name="search"
                                                        id="search"
                                                        className="focus:ring-primary-main rounded-full outline-0 focus:border-primary-main block w-full pl-10 text-md sm:text-sm bg-white dark:bg-darkBg dark:text-darkBg-mainText"
                                                        placeholder={searchPlaceHolder}
                                                        value={searchValue}
                                                        onChange={(e) => onSearchChange(e)}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* List */}
                                        {searchedList && <div className={`px-1 pb-2 ${searchedList.length > 4 ? 'overflow-y-scroll' : ''} max-h-[200px]`}>
                                            <>
                                                {(sort ? searchedList.sort() : searchedList).map((listItem, key) => (
                                                    <Listbox.Option
                                                        key={key}
                                                        className={({ active, selected }) =>
                                                            clsx(
                                                                active ? 'bg-primary-surface dark:bg-primary-surface' : 'text-gray-900 dark:text-darkBg-mainText',
                                                                'cursor-default select-none relative py-2 pl-3 pr-0 text-sm hover:cursor-pointer',
                                                                { ['bg-primary-main']: selected }
                                                            )
                                                        }
                                                        value={listItem.value}>
                                                        {({ selected }) => (
                                                            <div className="flex items-center  ">
                                                                <div className="">
                                                                    <div className="flex items-center justify-between  w-full">
                                                                        <span
                                                                            className={clsx(
                                                                                selected ? 'font-semibold' : 'font-normal',
                                                                                'ml-3 block truncate text-sm w-full'
                                                                            )}
                                                                        >
                                                                            {listItem.name}
                                                                        </span>

                                                                        {listItem.title && (
                                                                            <div className="flex items-center  whitespace-nowrap">
                                                                                {listItem.title}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {listItem.description && (
                                                                        <div className={clsx('flex ml-3 text-xs text-justify text-gray-400 dark:text-darkBg-subText italic')}>
                                                                            {listItem.description.length > 50 ? (listItem.description).slice(0, 50) + '...' : (listItem.description).slice(0, 50)}
                                                                        </div>
                                                                    )}
                                                                </div>


                                                                {selected ? (
                                                                    <span
                                                                        className={clsx(
                                                                            selected ? 'text-white ml-5' : 'text-gray-900'
                                                                            // 'absolute inset-y-0 right-0 flex items-center pr-1'
                                                                        )}>
                                                                        <CheckIcon className="h-5 w-5 font-bold" aria-hidden="true" />
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                        )}
                                                    </Listbox.Option>
                                                ))}
                                            </>
                                        </div>}
                                        {searchedList && searchedList?.length < 1 && <div className='text-lg font-semibold text-center text-gray-300 dark:text-gray-500 py-4'>Not found</div>}
                                    </>
                                )}
                            </Listbox.Options>
                        </Transition>
                    </div>

                    {/* Helper Text */}
                    <div className={clsx('text-xs text-gray-400 dark:text-darkBg-subText', { ['!text-danger-main']: error })}>
                        {helperText && (
                            <motion.p
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-2 text-sm text-red-600 flex items-center font-medium"
                            >
                                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {helperText && typeof helperText === 'string' ? helperText : undefined}
                            </motion.p>
                        )}
                    </div>

                    <ScrollToFormikError fieldName={name ?? ''} fieldRef={fieldRef} />
                </>
            )}
        </Listbox>
    );
}

export default IntermediarySelectorInput;
